import { useState, useEffect } from 'react'
import { ApiKeyCredentialForm } from '@/components/integrations/ApiKeyCredentialForm'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import { knowledgeApiClient } from '@/lib/api/knowledge.client'
import { toast } from 'sonner'
import { ROUTE_REGISTRY } from '@/lib/routes'
import { INTEGRATIONS, getIntegrationSyncConfig } from '@super/shared-types'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useAuth } from '@/hooks/use-auth'

const CLIENT_ID_DEFAULT = INTEGRATIONS.SHOWMOJO.CLIENT_ID_SENTINEL

export interface ShowMojoConnectFormProps {
  isEditMode?: boolean
  onConnected: () => void
}

export function ShowMojoConnectForm({
  isEditMode = false,
  onConnected,
}: ShowMojoConnectFormProps) {
  const { currentCompany } = useAppAuth()
  const { isSuperAdmin } = useAuth()
  const [existingCredential, setExistingCredential] = useState<{
    clientSecret: string
  } | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!isEditMode || !currentCompany?.id) return

    const fetchStatus = async () => {
      try {
        setIsLoading(true)
        const integrations = isSuperAdmin
          ? await integrationsClient.getAllAdminIntegrationsStatus(
              currentCompany.id,
            )
          : await integrationsClient.getAllIntegrationsStatus()
        const showMojo = integrations.find(
          i =>
            i.type === INTEGRATIONS.SHOWMOJO.TYPE ||
            i.name === INTEGRATIONS.SHOWMOJO.NAME,
        )
        if (showMojo?.connected) {
          setExistingCredential({ clientSecret: '' })
        }
      } catch (error) {
        console.error('Error fetching integration status:', error)
        toast.error('Failed to load existing credentials')
      } finally {
        setIsLoading(false)
      }
    }
    fetchStatus()
  }, [isEditMode, currentCompany?.id, isSuperAdmin])

  const handleSubmit = async (data: {
    clientId?: string
    clientSecret: string
    accountId?: string
  }) => {
    const { clientSecret } = data
    if (!currentCompany?.id) {
      toast.error('No company selected')
      return
    }

    try {
      if (!isEditMode) {
        const syncConfig = getIntegrationSyncConfig(INTEGRATIONS.SHOWMOJO.TYPE)
        if (
          syncConfig?.supportsSync &&
          syncConfig.syncTypes.includes('LISTINGS')
        ) {
          const existingUrlSync =
            isSuperAdmin && currentCompany?.id
              ? await knowledgeApiClient.getAdminListingUrlSyncStatus(
                  currentCompany.id,
                )
              : await knowledgeApiClient.getListingUrlSyncStatus()
          if (existingUrlSync) {
            if (isSuperAdmin && currentCompany?.id) {
              await knowledgeApiClient.removeAdminListingUrlSync(
                currentCompany.id,
              )
            } else {
              await knowledgeApiClient.removeListingUrlSync()
            }
          }
        }
      }

      if (isEditMode) {
        if (isSuperAdmin) {
          await integrationsClient.showMojo.updateAdminCredentials(
            currentCompany.id,
            CLIENT_ID_DEFAULT,
            clientSecret,
          )
        } else {
          await integrationsClient.showMojo.updateCredentials(
            CLIENT_ID_DEFAULT,
            clientSecret,
          )
        }
        toast.success('ShowMojo credentials updated successfully!')
      } else {
        if (isSuperAdmin) {
          await integrationsClient.showMojo.createAdminCredentials(
            currentCompany.id,
            CLIENT_ID_DEFAULT,
            clientSecret,
          )
        } else {
          await integrationsClient.showMojo.createCredentials(
            CLIENT_ID_DEFAULT,
            clientSecret,
          )
        }
        toast.success('ShowMojo integration connected successfully!', {
          description: (
            <div className="flex flex-col gap-1">
              <p>We are syncing your listings now.</p>
              <a
                href={`${ROUTE_REGISTRY.KNOWLEDGE.path}?tab=listings`}
                className="text-xs underline underline-offset-2 hover:opacity-70 transition-opacity"
              >
                View listings
              </a>
            </div>
          ),
        })
      }
      onConnected()
    } catch (error) {
      console.error(
        `Error ${isEditMode ? 'updating' : 'connecting'} ShowMojo:`,
        error,
      )
      toast.error(
        `Failed to ${isEditMode ? 'update' : 'connect'} ShowMojo integration`,
      )
      throw error
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
      </div>
    )
  }

  return (
    <ApiKeyCredentialForm
      onSubmit={handleSubmit}
      clientSecretConfig={{
        label: 'API Key',
        placeholder: 'Enter your ShowMojo API key',
        description:
          'You can find your API key in your ShowMojo account settings',
        initialValue: existingCredential?.clientSecret,
      }}
      submitButtonText={isEditMode ? 'Update' : 'Connect'}
      confirmationText={
        isEditMode
          ? 'Update your API key for ShowMojo'
          : 'Connect your API key for ShowMojo'
      }
      componentId="vendor-connect-showmojo"
    />
  )
}
